import React, { useState, useEffect } from 'react';
import { useAuth, UserProfile } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose }) => {
  const { user, profile, updateProfile, logout } = useAuth();
  const [username, setUsername] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [age, setAge] = useState<number | undefined>(undefined);
  const [description, setDescription] = useState('');
  const [theme, setTheme] = useState<'dark' | 'retro' | 'neon'>('dark');
  const [activeTab, setActiveTab] = useState<'general' | 'appearance'>('general');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  // Cargar datos del perfil existente
  useEffect(() => {
    if (profile) {
      setUsername(profile.username || '');
      setAvatarUrl(profile.avatar_url || '');
      setPreviewUrl(profile.avatar_url || null);
      setAge(profile.age);
      setDescription(profile.description || '');
      setTheme(profile.theme || 'dark');
    }
  }, [profile]);

  // Manejar clic fuera del modal para cerrarlo
  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Manejar la selección de archivos para la foto de perfil
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validar tipo y tamaño
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 2 * 1024 * 1024; // 2MB
    
    if (!validTypes.includes(file.type)) {
      setError('Formato de imagen no válido. Use JPG, PNG, GIF o WebP.');
      return;
    }
    
    if (file.size > maxSize) {
      setError('La imagen es demasiado grande. Máximo 2MB permitido.');
      return;
    }
    
    setError(null);
    setAvatarFile(file);
    
    // Crear URL para previsualización
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    
    // Limpiar URL al desmontar componente
    return () => URL.revokeObjectURL(objectUrl);
  };

  // Subir avatar a Supabase Storage
  const uploadAvatar = async (file: File): Promise<string> => {
    setUploading(true);
    try {
      if (!user) throw new Error('Usuario no autenticado');
      
      // Crear nombre de archivo único
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;
      
      // Subir archivo a Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (uploadError) throw uploadError;
      
      // Obtener URL pública
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      
      return data.publicUrl;
    } catch (error: any) {
      console.error('Error al subir avatar:', error.message);
      throw new Error(`Error al subir imagen: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    
    try {
      // Validar campos
      if (!username.trim()) {
        throw new Error('Por favor, ingrese un nombre de usuario');
      }
      
      let finalAvatarUrl = avatarUrl;
      
      // Si hay un nuevo archivo, subirlo
      if (avatarFile) {
        finalAvatarUrl = await uploadAvatar(avatarFile);
      }
      
      // Actualizar perfil
      await updateProfile({
        username: username.trim(),
        avatar_url: finalAvatarUrl,
        age,
        description: description.trim(),
        theme,
      });
      
      setSuccess('Perfil actualizado correctamente');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Manejar cierre de sesión
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error: any) {
      setError('Error al cerrar sesión: ' + error.message);
    }
  };

  // Manejar el cambio de tema
  const handleThemeChange = (newTheme: 'dark' | 'retro' | 'neon') => {
    setTheme(newTheme);
    
    // Aplicamos el tema inmediatamente para ver la vista previa
    document.body.classList.remove('theme-dark', 'theme-neon', 'theme-retro');
    document.body.classList.add(`theme-${newTheme}`);
    
    // Actualizar todas las variables CSS según el tema seleccionado
    if (newTheme === 'dark') {
      // Variables de fondo y color principal
      document.documentElement.style.setProperty('--bg-color', 'rgb(18, 18, 18)');
      document.documentElement.style.setProperty('--card-bg', 'rgba(18, 18, 18, 0.7)');
      document.documentElement.style.setProperty('--accent-color', '#3B82F6');
      
      // Variables de texto
      document.documentElement.style.setProperty('--text-color', '#ffffff');
      document.documentElement.style.setProperty('--text-secondary', 'rgba(209, 213, 219, 1)');
      
      // Variables de bordes y botones
      document.documentElement.style.setProperty('--border-color', 'rgba(55, 65, 81, 0.3)');
      document.documentElement.style.setProperty('--button-bg', 'rgba(59, 130, 246, 0.2)');
      document.documentElement.style.setProperty('--button-hover', 'rgba(59, 130, 246, 0.3)');
      document.documentElement.style.setProperty('--button-text', 'rgba(96, 165, 250, 1)');
      
      // Variables de input
      document.documentElement.style.setProperty('--input-bg', 'rgba(31, 41, 55, 0.7)');
      document.documentElement.style.setProperty('--input-border', 'rgba(55, 65, 81, 0.5)');
      document.documentElement.style.setProperty('--input-focus', 'rgba(59, 130, 246, 0.4)');
      
      // Variables de scrollbar
      document.documentElement.style.setProperty('--scrollbar-track', 'rgba(15, 23, 42, 0.3)');
      document.documentElement.style.setProperty('--scrollbar-thumb', 'rgba(59, 130, 246, 0.5)');
    } else if (newTheme === 'neon') {
      // Variables de fondo y color principal
      document.documentElement.style.setProperty('--bg-color', 'rgb(5, 5, 5)');
      document.documentElement.style.setProperty('--card-bg', 'rgba(5, 5, 5, 0.6)');
      document.documentElement.style.setProperty('--accent-color', '#00FFFF');
      
      // Variables de texto
      document.documentElement.style.setProperty('--text-color', '#ffffff');
      document.documentElement.style.setProperty('--text-secondary', 'rgba(209, 213, 219, 1)');
      
      // Variables de bordes y botones
      document.documentElement.style.setProperty('--border-color', 'rgba(0, 255, 255, 0.3)');
      document.documentElement.style.setProperty('--button-bg', 'rgba(0, 255, 255, 0.2)');
      document.documentElement.style.setProperty('--button-hover', 'rgba(0, 255, 255, 0.3)');
      document.documentElement.style.setProperty('--button-text', 'rgba(0, 255, 255, 1)');
      
      // Variables de input
      document.documentElement.style.setProperty('--input-bg', 'rgba(5, 5, 5, 0.7)');
      document.documentElement.style.setProperty('--input-border', 'rgba(0, 255, 255, 0.5)');
      document.documentElement.style.setProperty('--input-focus', 'rgba(0, 255, 255, 0.4)');
      
      // Variables de scrollbar
      document.documentElement.style.setProperty('--scrollbar-track', 'rgba(5, 5, 5, 0.5)');
      document.documentElement.style.setProperty('--scrollbar-thumb', 'rgba(0, 255, 255, 0.5)');
    } else if (newTheme === 'retro') {
      // Variables de fondo y color principal
      document.documentElement.style.setProperty('--bg-color', 'rgb(24, 0, 71)');
      document.documentElement.style.setProperty('--card-bg', 'rgba(24, 0, 71, 0.7)');
      document.documentElement.style.setProperty('--accent-color', '#DAA520');
      
      // Variables de texto
      document.documentElement.style.setProperty('--text-color', '#ffffff');
      document.documentElement.style.setProperty('--text-secondary', 'rgba(254, 243, 199, 1)');
      
      // Variables de bordes y botones
      document.documentElement.style.setProperty('--border-color', 'rgba(218, 165, 32, 0.3)');
      document.documentElement.style.setProperty('--button-bg', 'rgba(218, 165, 32, 0.2)');
      document.documentElement.style.setProperty('--button-hover', 'rgba(218, 165, 32, 0.3)');
      document.documentElement.style.setProperty('--button-text', 'rgba(251, 191, 36, 1)');
      
      // Variables de input
      document.documentElement.style.setProperty('--input-bg', 'rgba(24, 0, 71, 0.7)');
      document.documentElement.style.setProperty('--input-border', 'rgba(218, 165, 32, 0.5)');
      document.documentElement.style.setProperty('--input-focus', 'rgba(218, 165, 32, 0.4)');
      
      // Variables de scrollbar
      document.documentElement.style.setProperty('--scrollbar-track', 'rgba(24, 0, 71, 0.5)');
      document.documentElement.style.setProperty('--scrollbar-thumb', 'rgba(218, 165, 32, 0.5)');
    }
    
    console.log(`Vista previa de tema aplicada: ${newTheme}`);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleOutsideClick}
    >
      <div className="bg-gradient-to-br from-gray-900/95 to-gray-800/95 rounded-xl border border-gray-700/30 w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between p-4 border-b border-gray-700/30">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <svg className="w-6 h-6 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Ajustes de Perfil
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs de navegación */}
        <div className="flex border-b border-gray-700/30">
          <button
            className={`flex-1 py-3 px-4 font-medium flex items-center justify-center transition-all duration-300 ${
              activeTab === 'general'
                ? 'text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('general')}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            General
          </button>
          <button
            className={`flex-1 py-3 px-4 font-medium flex items-center justify-center transition-all duration-300 ${
              activeTab === 'appearance'
                ? 'text-purple-400 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('appearance')}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            Apariencia
          </button>
        </div>

        <div className="overflow-y-auto p-6 max-h-[calc(90vh-120px)]">
          {error && (
            <div className="mb-6 bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg flex items-center">
              <svg className="w-5 h-5 mr-2 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-6 bg-green-900/30 border border-green-700/50 text-green-300 px-4 py-3 rounded-lg flex items-center">
              <svg className="w-5 h-5 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {activeTab === 'general' && (
              <div className="space-y-6">
                {/* Avatar Preview & Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Foto de Perfil</label>
                  <div className="flex items-center">
                    <div className="relative mr-4">
                      <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-800/70 flex items-center justify-center border border-gray-700/50">
                        {previewUrl ? (
                          <img 
                            src={previewUrl} 
                            alt="Avatar Preview" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        )}
                      </div>
                      {uploading && (
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                          <svg className="animate-spin h-8 w-8 text-blue-400" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="relative group">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                        <div className="relative">
                          <label className="w-full flex items-center justify-center px-4 py-2 bg-gray-800/90 border border-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-800 transition-all">
                            <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-sm text-gray-300">Seleccionar imagen</span>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={handleFileChange}
                              disabled={uploading}
                            />
                          </label>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-gray-400">
                        JPG, PNG o GIF. Máximo 2MB.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Username Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nombre de usuario</label>
                  <div className="relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                    <div className="relative">
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="w-full bg-gray-800/90 border border-gray-700/50 rounded-xl px-5 py-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all duration-300"
                        placeholder="Tu nombre o nickname"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                {/* Age Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Edad</label>
                  <div className="relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                    <div className="relative">
                      <input
                        type="number"
                        value={age || ''}
                        onChange={(e) => setAge(e.target.value ? parseInt(e.target.value) : undefined)}
                        className="w-full bg-gray-800/90 border border-gray-700/50 rounded-xl px-5 py-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all duration-300"
                        placeholder="Tu edad (opcional)"
                        min={13}
                        max={120}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Description Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Descripción</label>
                  <div className="relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                    <div className="relative">
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full bg-gray-800/90 border border-gray-700/50 rounded-xl px-5 py-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all duration-300"
                        placeholder="Una breve descripción sobre ti (opcional)"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-4">Tema de la aplicación</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Tema Oscuro */}
                    <div 
                      className={`bg-gray-900 border ${theme === 'dark' ? 'border-blue-500' : 'border-gray-700'} rounded-xl p-4 cursor-pointer hover:border-blue-500/70 transition-all`}
                      onClick={() => handleThemeChange('dark')}
                    >
                      <div className="h-24 flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg mb-3 overflow-hidden">
                        <div className="w-8 h-8 bg-blue-500/70 rounded-lg"></div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white font-medium">Oscuro</span>
                        {theme === 'dark' && (
                          <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    
                    {/* Tema Retro */}
                    <div 
                      className={`bg-indigo-900/30 border ${theme === 'retro' ? 'border-amber-500' : 'border-gray-700'} rounded-xl p-4 cursor-pointer hover:border-amber-500/70 transition-all`}
                      onClick={() => handleThemeChange('retro')}
                    >
                      <div className="h-24 flex items-center justify-center bg-gradient-to-br from-indigo-900/40 to-purple-900/40 rounded-lg mb-3 overflow-hidden">
                        <div className="w-8 h-8 bg-amber-500/70 rounded-lg"></div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-amber-200 font-medium">Retro</span>
                        {theme === 'retro' && (
                          <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    
                    {/* Tema Neón */}
                    <div 
                      className={`bg-gray-900 border ${theme === 'neon' ? 'border-green-500' : 'border-gray-700'} rounded-xl p-4 cursor-pointer hover:border-green-500/70 transition-all`}
                      onClick={() => handleThemeChange('neon')}
                    >
                      <div className="h-24 flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-900 rounded-lg mb-3 overflow-hidden shadow-[inset_0_0_30px_rgba(34,197,94,0.2)]">
                        <div className="w-8 h-8 bg-green-500/70 rounded-lg shadow-[0_0_15px_rgba(34,197,94,0.5)]"></div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-green-300 font-medium">Neón</span>
                        {theme === 'neon' && (
                          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-900/10 border border-blue-700/30 rounded-lg p-4">
                  <h3 className="text-blue-300 font-medium flex items-center mb-2">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Vista previa de temas
                  </h3>
                  <p className="text-sm text-gray-300">
                    Los cambios de tema se aplicarán a toda la aplicación cuando guardes la configuración.
                  </p>
                </div>
              </div>
            )}

            <div className="mt-8 flex justify-between">
              <button
                type="button"
                onClick={handleLogout}
                className="px-4 py-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all duration-300 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Cerrar Sesión</span>
              </button>
              
              <button
                type="submit"
                disabled={loading || uploading}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600/80 to-blue-500/80 text-white hover:from-blue-500/80 hover:to-blue-400/80 shadow-blue-600/20 flex items-center justify-center space-x-2 disabled:opacity-50 shadow-lg transition-all duration-300"
              >
                {loading || uploading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Guardar Cambios</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal; 